import React, { useContext, useState } from 'react'
import { useTheme } from '@mui/material'
import moment from 'moment'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ShieldIcon from '@mui/icons-material/Shield'
import BlockIcon from '@mui/icons-material/Block'
import { translateMetaError } from '../../libs/metaErrors'
import { isMetaOfficialProvider } from '../../libs/metaTemplates'
import { getMessageStatus } from '../../libs/messageStatus'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import { coresDoTom } from './MessageStatusChip'
import MetaErrorLinks from './MetaErrorLinks'

// Estados que não são falha mas pedem uma frase na linha: sem ela o cliente vê
// a mensagem parada e não sabe se precisa fazer alguma coisa.
const STATUS_EXPLICADOS = new Set([
  'antiban_delayed',
  'saldo_insuficiente',
  'hiperpay_delayed',
  'suppressed',
  'smartfilter_delayed',
  'smartfilter_pending_approval',
  'cancelada',
])

// A linha precisa saber ANTES de renderizar se haverá aviso (um <div> vazio
// abre uma faixa na grade). A lista mora aqui, junto de quem escreve o aviso,
// pra as duas coisas não se desencontrarem.
export function temAvisoInline(message) {
  const estado = getMessageStatus(message)
  return estado.ehFalha || estado.entregueApesarDoErro || STATUS_EXPLICADOS.has(message?.status)
}

// O motivo do erro sobe para a linha porque é a pergunta que faz o operador
// abrir a mensagem. Sobe só o título e a ação: a explicação longa e o texto
// técnico continuam no expandir, que é onde se investiga um caso.
export default function MessageErrorInline({ message }) {
  const theme = useTheme()
  const { provider } = useContext(ApiDataWrapperContext)
  const [mostrarTecnico, setMostrarTecnico] = useState(false)
  const estado = getMessageStatus(message)

  if (estado.entregueApesarDoErro) {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={DoneAllIcon}>
        <b>Entregue ao destinatário{message.ack >= 3 ? ' e lida' : ''}.</b>{' '}
        Uma tentativa anterior falhou e o motivo ficou registrado — não é preciso reenviar.
      </Aviso>
    )
  }

  if (message.status === 'antiban_delayed') {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={WatchLaterIcon}>
        <b>Adiada pelo anti-ban</b> (teto diário ou aquecimento do número). Volta sozinha
        {message.sendAt ? ` a partir de ${moment(message.sendAt).format('DD/MM [às] HH:mm')}` : ''} — não precisa reenviar.
      </Aviso>
    )
  }

  if (message.status === 'saldo_insuficiente') {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={AccountBalanceWalletIcon}>
        <b>Retida por saldo insuficiente.</b> A linha de crédito desta API ficou sem saldo;
        a mensagem sai sozinha depois da recarga, se o saldo novo pagar por ela (as mais antigas saem primeiro;
        válida por até 24h) — não precisa reenviar.
      </Aviso>
    )
  }

  if (message.status === 'hiperpay_delayed') {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={WatchLaterIcon}>
        <b>Aguardando o link de pagamento.</b> O serviço que gera o link (HiperPay) não respondeu;
        a mensagem tenta de novo sozinha{message.sendAt ? ` a partir de ${moment(message.sendAt).format('DD/MM [às] HH:mm')}` : ''} — não
        precisa reenviar. Se não sair em até 24h, ela aparece como erro para você reenviar.
      </Aviso>
    )
  }

  if (message.status === 'suppressed') {
    const cores = coresDoTom('mudo', theme)
    return (
      <Aviso cores={cores} Icone={GppMaybeIcon}>
        <b>Não enviada:</b> o destinatário pediu para não receber mais mensagens.
      </Aviso>
    )
  }

  // O sendAt de uma retida é quando ela volta a sair. "A partir de" porque, se
  // o expediente da API abrir mais tarde, ela espera o expediente.
  if (message.status === 'smartfilter_delayed') {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={ShieldIcon}>
        <b>Adiada pelo SmartFilter.</b> Uma regra da sua conta (como a de horário) segurou o envio;
        ela sai sozinha{message.sendAt ? ` a partir de ${moment(message.sendAt).format('DD/MM [às] HH:mm')}` : ''} — não precisa reenviar.
      </Aviso>
    )
  }

  if (message.status === 'smartfilter_pending_approval') {
    const cores = coresDoTom('espera', theme)
    return (
      <Aviso cores={cores} Icone={ShieldIcon}>
        <b>Aguardando conferência no SmartFilter.</b> Ela só sai depois que alguém aprovar na aba SmartFilter.
      </Aviso>
    )
  }

  if (message.status === 'cancelada') {
    const cores = coresDoTom('mudo', theme)
    return (
      <Aviso cores={cores} Icone={BlockIcon}>
        <b>Cancelada pelo cliente.</b> O disparo foi cancelado antes de esta mensagem sair — ela não foi
        enviada e não foi cobrada.
      </Aviso>
    )
  }

  if (!estado.ehFalha) return null

  const cores = coresDoTom('erro', theme)
  const amigavel = isMetaOfficialProvider(provider) ? translateMetaError(message.erro) : null

  if (!amigavel) {
    const texto = message.erro?.text
    if (!texto) return null
    return <Aviso cores={cores} Icone={ReportProblemIcon}>{texto}</Aviso>
  }

  return (
    <Aviso cores={cores} Icone={ReportProblemIcon}>
      <span className="font-bold">
        {amigavel.titulo}
        {amigavel.code ? <span className="font-normal opacity-75"> · {amigavel.code}</span> : null}
      </span>
      {amigavel.acao ? <span className="block opacity-90">{amigavel.acao}</span> : null}
      <MetaErrorLinks tecnico={amigavel.tecnico} />
      {amigavel.tecnico ? (
        <>
          <button
            type="button"
            className="mt-1 text-[11px] underline opacity-80"
            onClick={(e) => { e.stopPropagation(); setMostrarTecnico((v) => !v) }}
          >
            {mostrarTecnico ? 'ocultar detalhe técnico' : 'ver detalhe técnico'}
          </button>
          {mostrarTecnico && <span className="block text-[11px] opacity-80 break-words">{amigavel.tecnico}</span>}
        </>
      ) : null}
    </Aviso>
  )
}

function Aviso({ cores, Icone, children }) {
  return (
    <div
      className="mt-2 flex items-start gap-2 rounded px-2.5 py-1.5 text-xs leading-snug"
      style={{ backgroundColor: cores.fundo, color: cores.texto }}
    >
      <Icone style={{ fontSize: 15, flex: 'none', marginTop: 1 }} />
      <span className="flex-1">{children}</span>
    </div>
  )
}
